import type { LocalDate, LocalTime } from '@konvert/shared';
import { toLocalDate } from './civil.js';

/**
 * Timezone conversions built on `Intl`, so the tz database ships with Node and
 * DST rules are never hard-coded (SPEC 5.6).
 */

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

interface Wall {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function wallClock(instant: Date, timeZone: string): Wall {
  const parts = formatterFor(timeZone).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((p) => p.type === type);
    if (!part) throw new Error(`missing date part ${type}`);
    return Number(part.value);
  };
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

/** Offset of `timeZone` at `instant`, in milliseconds east of UTC. */
export function offsetMs(instant: Date, timeZone: string): number {
  const w = wallClock(instant, timeZone);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  const instantSeconds = Math.floor(instant.getTime() / 1000) * 1000;
  return asUtc - instantSeconds;
}

export function localDateOf(instant: Date, timeZone: string): LocalDate {
  const w = wallClock(instant, timeZone);
  return toLocalDate(Date.UTC(w.year, w.month - 1, w.day));
}

export function localTimeOf(instant: Date, timeZone: string): LocalTime {
  const w = wallClock(instant, timeZone);
  return `${String(w.hour).padStart(2, '0')}:${String(w.minute).padStart(2, '0')}`;
}

/**
 * Wall-clock time in a timezone -> instant.
 *
 * Both plausible offsets are tried. When the local time exists twice (autumn
 * transition) the earlier instant wins; when it does not exist at all (spring
 * transition) the time is shifted forward past the gap.
 */
export function zonedTimeToInstant(date: LocalDate, time: LocalTime, timeZone: string): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const [hh, mm] = time.split(':').map(Number) as [number, number];
  const naive = Date.UTC(y, m - 1, d, hh, mm, 0);
  const DAY = 86_400_000;

  // Sample the offsets in force around the requested time; on a DST day the two
  // differing offsets yield the two possible instants for the same wall clock.
  const offsets = [naive - DAY, naive, naive + DAY].map((t) => offsetMs(new Date(t), timeZone));
  const candidates = [...new Set(offsets.map((o) => naive - o))].sort((a, b) => a - b);

  const matching = candidates.filter((ts) => {
    const w = wallClock(new Date(ts), timeZone);
    return w.hour === hh && w.minute === mm && w.day === d;
  });

  if (matching.length > 0) return new Date(matching[0]!);

  // The wall clock does not exist (spring-forward gap): shift past the gap.
  const fallbackOffset = offsetMs(new Date(naive - offsets[1]!), timeZone);
  return new Date(naive - fallbackOffset);
}

/** Midnight that opens the local day (SPEC 16: day starts at local midnight). */
export function startOfLocalDay(date: LocalDate, timeZone: string): Date {
  return zonedTimeToInstant(date, '00:00', timeZone);
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}
